import { ApiRepository } from './base_repository';
import { FeedbackToastHandler } from '../../config/errors/feedback';
import { dataAxiosInstance } from '../../utils/axios/axios';
import {
  DataAPIPrefix,
  LISTING_TYPES,
  LISTING_TYPES_NAME,
} from '../../config/constants';
import {
  AmazonScrappedListingType,
  SearchEngineQueryShortResult,
} from '../../data/types/listing_scrapers';
import { AttributeAssociation, Listing } from '../../data/types/listings';
import { toast } from 'react-toastify';
import SharedRepository from './shared_repository';
import { AttributeType } from '../../data/types/attributes';
import axios from 'axios';

class DataApiRepository extends ApiRepository {
  private static instance: DataApiRepository | null = null;
  constructor(axiosInstance: any) {
    super(axiosInstance);
  }

  public static getInstance(axiosInstance: any): DataApiRepository {
    if (!DataApiRepository.instance) {
      DataApiRepository.instance = new DataApiRepository(axiosInstance);
    }
    return DataApiRepository.instance;
  }

  searchForQuery = async ({
    query,
    country,
    useCache,
    demo = false,
  }: {
    query: string;
    country: string;
    useCache?: boolean;
    demo?: boolean;
  }): Promise<SearchEngineQueryShortResult[] | false> => {
    try {
      if (demo) {
        // Wait 5 seconds to simulate a real request
        await new Promise((resolve) => setTimeout(resolve, 2000));
        return countriesDemo;
      }
      const res = await FeedbackToastHandler(
        this.sendRequest('POST', `/search`, {
          baseApiUrl: DataAPIPrefix,
          data: {
            query,
            country,
            useCache,
          },
        }),
        {
          noSuccessMsg: true,
          noPendingMsg: true,
        },
      );

      return res.data as SearchEngineQueryShortResult[];
    } catch (error: any) {
      return false;
    }
  };

  getASINDetails = async ({
    asin,
    country,
    demo = false,
  }: {
    asin: string;
    country: string;
    demo?: boolean;
  }): Promise<AmazonScrappedListingType | false> => {
    try {
      if (demo) {
        // Wait 5 seconds to simulate a real request
        await new Promise((resolve) => setTimeout(resolve, 5000));
        return listinRes;
      }
      const res = await FeedbackToastHandler(
        this.sendRequest('POST', `/listing-new`, {
          baseApiUrl: DataAPIPrefix,
          data: {
            id: asin,
            country: country,
            soruce: 0,
          },
        }),
        {
          noSuccessMsg: true,
          noPendingMsg: true,
        },
      );

      const base_attributes_names = new Set(
        res.data.attributes.silal_attributes.map((attr: any) => attr.name_en),
      );

      // Remove any attribute in custom_attributes with the same name as an attribute in base_attributes
      res.data.attributes.unofficial_attributes =
        res.data.attributes.unofficial_attributes.filter(
          (attr: any) => !base_attributes_names.has(attr.name_en),
        );
      console.log(res.data);
      return res.data;
    } catch (error: any) {
      return false;
    }
  };

  aiImageAndTextToListing = async ({
    description,
    photos,
    demo = false,
  }: {
    description: string;
    photos: string[];
    demo?: boolean;
  }): Promise<AmazonScrappedListingType | false> => {
    try {
      if (demo) {
        // Wait 5 seconds to simulate a real request
        await new Promise((resolve) => setTimeout(resolve, 5000));
        return listinRes;
      }
      const res = await FeedbackToastHandler(
        this.sendRequest('POST', `/general-listing`, {
          baseApiUrl: DataAPIPrefix,
          data: {
            description,
            photos,
          },
        }),
        {
          noSuccessMsg: true,
          noPendingMsg: true,
        },
      );

      const base_attributes_names = new Set(
        res.data.attributes.silal_attributes.map((attr: any) => attr.name_en),
      );

      // Remove any attribute in custom_attributes with the same name as an attribute in base_attributes
      res.data.attributes.unofficial_attributes =
        res.data.attributes.unofficial_attributes.filter(
          (attr: any) => !base_attributes_names.has(attr.name_en),
        );
      console.log(res.data);
      return res.data as AmazonScrappedListingType;
    } catch (error: any) {
      return false;
    }
  };

  // ? Deprecated
  amazonDataScrapper = async ({
    query,
    limit,
  }: {
    query: string;
    limit: number;
  }): Promise<AmazonScrappedListingType[] | false> => {
    try {
      const res = await FeedbackToastHandler(
        this.sendRequest('POST', `/product`, {
          baseApiUrl: DataAPIPrefix,
          data: {
            query,
            limit,
          },
        }),
        {
          noSuccessMsg: true,
          noPendingMsg: true,
        },
      );

      return res.data as AmazonScrappedListingType[];
    } catch (error: any) {
      return false;
    }
  };

  convertToTemplate = async (
    amazonProduct: AmazonScrappedListingType,
  ): Promise<Listing> => {
    toast.info('Building template from data, please wait');
    var listing = {};
    function processTemplateInfo() {
      listing = {
        id: 0,
        listing_type_name:
          LISTING_TYPES_NAME[
            LISTING_TYPES.template as keyof typeof LISTING_TYPES_NAME
          ],
        listing_type_value: LISTING_TYPES.template,
      };
    }

    function processListingInfo() {
      listing = {
        ...listing,
        name_en: amazonProduct.titles?.title_en || '',
        name_ar: amazonProduct.titles?.title_ar || '',
        rich_text_description_en: amazonProduct.titles?.description_en || '',
        rich_text_description_ar: amazonProduct.titles?.description_ar || '',
        is_used: false,
        category_id: amazonProduct.category?.id || 1,
      };
    }

    function processMedia() {
      listing = {
        ...listing,
        images: amazonProduct?.frontEndAddedImageObjects || [],
        cover_image_id:
          amazonProduct?.frontEndAddedImageObjects?.map((e) => e.id)?.[0] || '',
      };
    }

    async function processSpecifications() {
      const { attributes } = await SharedRepository.getCategoryDetails(
        amazonProduct.category?.id || 0,
      ).then((res) => {
        if (!res) {
          toast.error(
            'Failed to fetch category details, no attributes were added',
          );
          return {
            attributes: [],
          };
        }
        return {
          attributes: res.attributes,
        };
      });

      function processAttributes() {
        function processOfficialAttributes() {
          let output: any[] = [];
          attributes.forEach((attr) => {
            amazonProduct.attributes.silal_attributes.forEach((temp) => {
              if (attr.id === temp.id) {
                const attr_val_en =
                  attr.type === AttributeType.BOOLEAN
                    ? temp.value
                    : attr.type === AttributeType.NUMERIC
                      ? temp?.value?.toString()
                      : temp.value;
                const attr_val_ar =
                  attr.type === AttributeType.BOOLEAN
                    ? temp.value
                    : attr.type === AttributeType.NUMERIC
                      ? temp?.value?.toString()
                      : attr.official_values_ar && attr.official_values_en
                        ? attr.official_values_ar[
                            attr.official_values_en.indexOf(
                              temp.value as string,
                            )
                          ]
                        : '';
                // In create listing, we check that the attribute is defined/undefined to determine if it's filled or not, i.e if the attribute Color with value "" -empty value- is detected, the flow assumes that it's filled with this value, so, to prevent that, we check in advance if the value is in attr_values, if not, we don't add it to the output at all and hence in the create listing flow, it will be considered as not filled.
                if (
                  attr.type === AttributeType.BOOLEAN ||
                  (attr.type === AttributeType.SELECT &&
                    attr_val_en !== null &&
                    attr.official_values_en?.includes(attr_val_en as string)) ||
                  (attr.type === AttributeType.NUMERIC &&
                    attr_val_en !== null &&
                    attr_val_en !== undefined)
                )
                  output.push({
                    attr_id: attr.id,
                    attr_name_en: attr.name_en,
                    attr_name_ar: attr.name_ar,
                    attr_value_en: (attr_val_en as boolean | string) ?? '',
                    attr_value_ar: (attr_val_ar as boolean | string) ?? '',
                    is_attribute_custom: false,
                    is_variant: false,
                    values_images_items: [],
                    attribute_full_object: {
                      id: attr.id,
                      category_id: attr.category_id,
                      name_en: attr.name_en,
                      name_ar: attr.name_ar,
                      official_values_en: attr.official_values_en,
                      official_values_ar: attr.official_values_ar,
                      priority: attr.priority,
                      type: attr.type,
                      type_name: attr.type_name,
                    },
                  });
              }
            });
          });
          return output;
        }

        function processCustomAttributes() {
          let output: any[] = [];
          const attrType = AttributeType.SELECT; // Backend sets the type to select for custom attributes
          amazonProduct.attributes?.unofficial_attributes?.forEach(
            (attr, i) => {
              output.push({
                attr_id: attrType * 10000 + i,
                attr_name_en: attr.name_en,
                attr_name_ar: '',
                attr_value_en: attr.value,
                attr_value_ar: '',
                is_attribute_custom: true,
                is_variant: false,
                values_images_items: [],
                attribute_full_object: {
                  id: attrType * 10000 + i,
                  category_id: amazonProduct.category.id,
                  name_en: attr.name_en,
                  name_ar: '',
                  official_values_en: [attr.value],
                  official_values_ar: [attr.value],
                  priority: 5,
                  type: AttributeType.SELECT,
                  type_name: attr.type_name,
                },
              });
            },
          );
          return output;
        }

        let output: any[] = [];
        const official_attrs: AttributeAssociation[] =
          processOfficialAttributes();
        let custom_attrs: AttributeAssociation[] = processCustomAttributes();

        attributes.forEach((attr) => {
          amazonProduct.attributes.silal_attributes.forEach((temp) => {
            if (attr.id === temp.id) {
              const attr_val_en =
                attr.type === AttributeType.BOOLEAN
                  ? temp.value
                  : attr.type === AttributeType.NUMERIC
                    ? temp?.value?.toString()
                    : temp.value;
              const attr_val_ar =
                attr.type === AttributeType.BOOLEAN
                  ? temp.value
                  : attr.type === AttributeType.NUMERIC
                    ? temp?.value?.toString()
                    : attr.official_values_ar && attr.official_values_en
                      ? attr.official_values_ar[
                          attr.official_values_en.indexOf(temp.value as string)
                        ]
                      : '';
              output.push({
                item_id: undefined,
                attr_id: attr.id,
                attr_value_image: undefined,
                attr_name_en: attr.name_en,
                attr_name_ar: attr.name_ar,
                is_attribute_custom: false,
                attr_value_en: (attr_val_en as boolean | string) ?? '',
                attr_value_ar: (attr_val_ar as boolean | string) ?? '',
                is_variant: false,
                attribute_type: attr.type,
                attribute_type_name: attr.type_name,
                unit: attr.unit || undefined,
              });
            }
          });
        });

        // Remove duplicates in custom attributes that are already in official attributes
        const attributes_names_set = new Set(
          official_attrs.map((attr) => attr.attr_name_en),
        );
        custom_attrs = custom_attrs.filter(
          (attr) => !attributes_names_set.has(attr.attr_name_en),
        );

        // Add custom attributes that are not in official attributes
        custom_attrs.forEach((attr) => {
          // if custom attribute is not in official attributes, add it to the output
          if (!attributes_names_set.has(attr.attr_name_en)) {
            output.push({
              item_id: undefined,
              attr_id: attr.attribute_full_object.id,
              attr_value_image: undefined,
              attr_name_en: attr.attr_name_en,
              attr_name_ar: '',
              is_attribute_custom: attr.is_attribute_custom,
              attr_value_en: attr.attr_value_en,
              attr_value_ar: '',
              is_variant: false,
              attribute_type: attr.attribute_full_object.type,
              attribute_type_name: attr.attribute_full_object.type_name,
              unit: attr.attribute_full_object.unit || undefined,
            });
          }
        });

        listing = {
          ...listing,
          official_attributes: official_attrs,
          custom_attributes: custom_attrs,
          attributes: output as any,
        };
      }

      processAttributes();
    }

    function processShippingInfo() {
      listing = {
        ...listing,
        weight: amazonProduct.dimensions.weight_g?.toString() || '',
        length: amazonProduct.dimensions.length_cm?.toString() || '',
        height: amazonProduct.dimensions.height_cm?.toString() || '',
        width: amazonProduct.dimensions.width_cm?.toString() || '',
        whats_included: amazonProduct.dimensions?.box_contents || '',
      };
    }

    processTemplateInfo();
    processListingInfo();
    processMedia();
    await processSpecifications();
    processShippingInfo();

    return listing as Listing;
  };
}

const DataRepository = DataApiRepository.getInstance(dataAxiosInstance);
export default DataRepository;

const listinRes = {
  titles: {
    title_en:
      'GTRACING Gaming Chair with Footrest in Navy Blue - Ergonomic Comfort for Gamers',
    title_ar:
      'كرسي ألعاب جي تي ريسينغ مع مسند قدم - أزرق بحري، راحة مريحة للاعبين',
    description_en:
      '<b>GTRACING Gaming Chair</b><br><br>Experience ultimate gaming comfort with the GTRACING Gaming Chair in Navy Blue. Designed for professional gamers, this chair enhances your gaming experience with its multifunctional features and ergonomic design.<br><br><b>Key Features:</b><ul><li>Adjustable armrest and seat height for personalized comfort.</li><li>Large reclining and rocking capabilities for relaxation.</li><li>360-degree swivel for easy movement.</li><li>High-quality PU leather and cushion support with lumbar headrest pillow.</li></ul><b>Specifications:</b><ul><li>Dimensions: 55.88 cm x 55.88 cm x 119.38 cm</li><li>Maximum Weight Recommendation: 45 kg</li><li>Material: PU Leather, Sponge Fill</li><li>Color: Navy Blue with F</li><li>Style: Racing</li></ul><b>Perfect For:</b><ul><li>Gaming, studying, and working.</li><li>Use in game rooms or at the desk.</li></ul><br>Enhance your game time or working hours with the supportive design of this chair, built to let you perform at your best.',
    description_ar:
      '<b>كرسي الألعاب جي تي ريسينغ</b><br><br>اختبر الراحة القصوى مع كرسي الألعاب جي تي ريسينغ بالأزرق البحري. تم تصميم هذا الكرسي للاعبين المحترفين، ليعزز تجربتك في اللعب من خلال ميزاته المتعددة والراحة المريحة.<br><br><b>الميزات الرئيسية:</b><ul><li>إمكانية ضبط مسند الذراع وارتفاع المقعد لراحة شخصية.</li><li>إمكانية الاستلقاء والتأرجح الكبيرة للاسترخاء.</li><li>دوران 360 درجة لحركة سهلة.</li><li>جلد صناعي عالي الجودة مع وسادة دعم الظهر والرأس.</li></ul><b>المواصفات:</b><ul><li>الأبعاد: 55.88 سم × 55.88 سم × 119.38 سم</li><li>الوزن الأقصى الموصى به: 45 كجم</li><li>المواد: جلد صناعي، حشوة إسفنجية</li><li>اللون: أزرق بحري مع F</li><li>الطراز: سباق</li></ul><b>مثالي لأجل:</b><ul><li>الألعاب، الدراسة، والعمل.</li><li>الاستخدام في غرف اللعب أو على المكتب.</li></ul><br>عزز وقت اللعب أو ساعات العمل مع تصميم هذا الكرسي الداعم، المصمم ليمكنك من الأداء بأفضل ما لديك.',
  },
  dimensions: {
    height_cm: 119.38,
    width_cm: 55.88,
    length_cm: 55.88,
    weight_g: 13607.73,
  },
  category: {
    id: 228,
    name: 'CATEGORIES_ROOT>GAMING',
  },
  attributes: {
    silal_attributes: [
      {
        id: 260,
        name_en: 'Boolean O',
        type_name: 'boolean',
        value: 'false',
      },
      {
        id: 261,
        name_en: 'Numeric O',
        type_name: 'numeric',
        value: '30',
      },
      {
        id: 259,
        name_en: 'Text O',
        type_name: 'select',
        value: 'Navy Blue With F',
      },
    ],
    unofficial_attributes: [
      {
        id: 262,
        name_en: 'Brand',
        type_name: 'select',
        value: 'GTRACING',
      },
      {
        id: 263,
        name_en: 'Size',
        type_name: 'select',
        value: 'with Footrest',
      },
      {
        id: 264,
        name_en: 'Back Style',
        type_name: 'select',
        value: 'Solid Back',
      },
      {
        id: 265,
        name_en: 'Special Feature',
        type_name: 'select',
        value: 'Arm Rest, Cushion Availability',
      },
      {
        id: 266,
        name_en: 'Product Care Instructions',
        type_name: 'select',
        value: 'Wipe Clean',
      },
      {
        id: 267,
        name_en: 'Recommended Uses For Product',
        type_name: 'select',
        value: 'Gaming',
      },
      {
        id: 268,
        name_en: 'Maximum Weight Recommendation',
        type_name: 'select',
        value: '100 Pounds',
      },
      {
        id: 269,
        name_en: 'Style',
        type_name: 'select',
        value: 'Racing-style',
      },
      {
        id: 270,
        name_en: 'Pattern',
        type_name: 'select',
        value: 'Solid',
      },
      {
        id: 271,
        name_en: 'Finish Type',
        type_name: 'select',
        value: 'Brushed',
      },
      {
        id: 272,
        name_en: 'Room Type',
        type_name: 'select',
        value: 'Game Recreation Room',
      },
      {
        id: 273,
        name_en: 'Age Range (Description)',
        type_name: 'select',
        value: 'Adult',
      },
      {
        id: 274,
        name_en: 'Included Components',
        type_name: 'select',
        value: 'Chair',
      },
      {
        id: 275,
        name_en: 'Theme',
        type_name: 'select',
        value: 'Video Game',
      },
      {
        id: 276,
        name_en: 'Shape',
        type_name: 'select',
        value: 'S-Shape',
      },
      {
        id: 277,
        name_en: 'Arm Style',
        type_name: 'select',
        value: 'With arms',
      },
      {
        id: 278,
        name_en: 'Surface Recommendation',
        type_name: 'select',
        value: 'Hard Floor',
      },
      {
        id: 279,
        name_en: 'Furniture Base Movement',
        type_name: 'select',
        value: 'Swivel',
      },
      {
        id: 280,
        name_en: 'Indoor/Outdoor Usage',
        type_name: 'select',
        value: 'Indoor',
      },
      {
        id: 281,
        name_en: 'Furniture Finish',
        type_name: 'select',
        value: 'Leather',
      },
      {
        id: 282,
        name_en: 'Fill Material',
        type_name: 'select',
        value: 'Sponge',
      },
      {
        id: 283,
        name_en: 'Cushion Style',
        type_name: 'select',
        value: 'Square-shaped or Round-shaped',
      },
      {
        id: 284,
        name_en: 'Leg Style',
        type_name: 'select',
        value: 'Straight Leg',
      },
      {
        id: 285,
        name_en: 'Reclining Position Count',
        type_name: 'select',
        value: 'Multiple',
      },
      {
        id: 286,
        name_en: 'Tilting',
        type_name: 'select',
        value: 'Yes',
      },
      {
        id: 287,
        name_en: 'Is Customizable',
        type_name: 'select',
        value: 'No',
      },
      {
        id: 288,
        name_en: 'Is Electric',
        type_name: 'select',
        value: 'No',
      },
      {
        id: 289,
        name_en: 'Is Foldable',
        type_name: 'select',
        value: 'No',
      },
      {
        id: 290,
        name_en: 'Included Throw Pillow Quantity',
        type_name: 'select',
        value: '2',
      },
      {
        id: 291,
        name_en: 'Form Factor',
        type_name: 'select',
        value: 'Recliner',
      },
      {
        id: 292,
        name_en: 'Manufacturer',
        type_name: 'select',
        value: 'GTRACING',
      },
      {
        id: 293,
        name_en: 'Country of Origin',
        type_name: 'select',
        value: 'China',
      },
      {
        id: 294,
        name_en: 'Item model number',
        type_name: 'select',
        value: 'GT002F',
      },
    ],
  },
  images: [
    'https://m.media-amazon.com/images/I/618I64n2QdL._AC_SL1500_.jpg',
    'https://m.media-amazon.com/images/I/71HFMGf0MxL._AC_SL1500_.jpg',
    'https://m.media-amazon.com/images/I/71CKRj97gGL._AC_SL1500_.jpg',
    'https://m.media-amazon.com/images/I/81PRz26YjpL._AC_SL1500_.jpg',
    'https://m.media-amazon.com/images/I/81glZU3-GqL._AC_SL1500_.jpg',
    'https://m.media-amazon.com/images/I/714D9JBHP7L._AC_SL1500_.jpg',
    'https://m.media-amazon.com/images/I/713lpRPOO7L._AC_SL1500_.jpg',
  ],
};

const countriesDemo = [
  {
    title: 'موبايل ايفون 15 من Hبل، (128 جيجابايت) - أزرق، بشريحتين',
    photo:
      'https://m.media-amazon.com/images/I/71k38LlSqML._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CHX1KLV5',
    asin: 'B0CHX1KLV5',
  },
  {
    title:
      'جراب مغناطيسي لموبايل ايفون 15 برو من اكس تي كيس متوافق مع ماج سيف، جراب موبايل شفاف غير لامع لموبايل ايفون 15 برو، ظهر من البولي كربونات مقاوم للخدش + ممتص للصدمات من البولي يوريثين الحراري، أسود',
    photo:
      'https://m.media-amazon.com/images/I/616MXNMwLtL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CFTZ2PBJ',
    asin: 'B0CFTZ2PBJ',
  },
  {
    title: 'موبايل آبل آيفون 15 128 جيجابايت، لون أخضر، ايفون 15، بشريحتين',
    photo:
      'https://m.media-amazon.com/images/I/712i-Bxl8qL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CHXLF6TL',
    asin: 'B0CHXLF6TL',
  },
  {
    title:
      'جراب اونيكس متوافق مع ايفون 15 برو من رينجكي، قبضة ممتازة، مضاد للانزلاق، مضاد للصدمات، جراب متين مثالي مع حماية للكاميرا - رمادي، بولي يوريثين',
    photo:
      'https://m.media-amazon.com/images/I/81KgDJJ9XaL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CTRY29YS',
    asin: 'B0CTRY29YS',
  },
  {
    title:
      'جراب متوافق مع ايفون 15 برو (6.1 بوصة) 15 سم، جراب شفاف كريستالي [لا يصفر] [حماية من السقوط من الدرجة العسكرية] جراب حماية شفاف مضاد للصدمات - أسود من دلـع موبايلك، البولي يوريثين الحراري',
    photo:
      'https://m.media-amazon.com/images/I/71k4G9G+ouL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CMFZRF8Y',
    asin: 'B0CMFZRF8Y',
  },
  {
    title:
      'جراب فيوجن بولد مطفي متوافق مع ايفون 15 برو 6.1 بوصة (2023) من رينجكي، قبضة قوية [نمط الإطار] تصميم خلفي مصنفر صلب مضاد للصدمات ومضاد للاصفرار، لون رمادي',
    photo:
      'https://m.media-amazon.com/images/I/71sXWISkHGL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CF2PZ6GL',
    asin: 'B0CF2PZ6GL',
  },
  {
    title:
      'دبليو اتش بي اكس جيه واي [قطعتان من واقي شاشة الخصوصية لموبايل ايفون 15 وايفون 15 برو، مضاد للتجسس، مضاد للخدش، حساس للمس، خالي من الفقاعات، سهل التركيب، ملحقات شاشة من الزجاج المقوى، أسود',
    photo:
      'https://m.media-amazon.com/images/I/71SdszFTrXL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CC2BR5LH',
    asin: 'B0CC2BR5LH',
  },
  {
    title:
      'جراب حماية كامل من رينجكي أونيكس متوافقة مع موبايل ايفون 15 برو، [حماية من الدرجة العسكرية] [قبضة مذهلة] من البولي يوريثين الحراري المقاوم للصدمات - أسود مطفي',
    photo:
      'https://m.media-amazon.com/images/I/81Irj3ygUlL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CHC65N59',
    asin: 'B0CHC65N59',
  },
  {
    title:
      'جراب متوافق مع ايفون 15 برو 6.1 بوصة من دلـ3 موبايلك، موبايل رفيع بتغطية كاملة السيليكون السائل 3 طبقات بغطاء مطاطي الجل الناعم بطانة المايكروفايبر المضاد للخدش (أبيض)، حافظة بسيطة، سيليكون مطاط جل',
    photo:
      'https://m.media-amazon.com/images/I/61nVy5Q4XrL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CNJRB3BY',
    asin: 'B0CNJRB3BY',
  },
  {
    title:
      'جراب لموبايل ايفون 15 برو من ايموديل® بتصميم نحيف من السيليكون الناعم غير اللامع لموبايل ايفون 15 برو (احمر)',
    photo:
      'https://m.media-amazon.com/images/I/712vLUxBirL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CTKW3NHK',
    asin: 'B0CTKW3NHK',
  },
  {
    title:
      'شاشة حماية زجاجية ثلاثية الابعاد مات مضادة للتوهج ومضادة لبصمات الاصابع متوافقة مع سلسلة ايفون 15 (ايفون 15 برو)، أسود',
    photo:
      'https://m.media-amazon.com/images/I/71JeB24i8mL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CVFTB5T1',
    asin: 'B0CVFTB5T1',
  },
  {
    title:
      'جراب مغناطيسي شفاف لموبايل ايفون 15 برو 6.1 بوصة من يوايدج، متوافق مع ماج سيف، موبايل رفيع للغاية وناعم من السيليكون والبولي يوريثين الحراري مضاد للصدمات ومقاوم للخدش والانزلاق، مطاط',
    photo:
      'https://m.media-amazon.com/images/I/61RoRuNk0kL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CFVF2ZCM',
    asin: 'B0CFVF2ZCM',
  },
  {
    title:
      'جراب حماية من السيليكون الناعم، مع بطانة من المايكروفايبر، لموبايل ابل ايفون 15 برو 6.1 بوصة، لون أسود',
    photo:
      'https://m.media-amazon.com/images/I/51U4jeoLsSL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CKHG79HC',
    asin: 'B0CKHG79HC',
  },
  {
    title:
      'جراب غوريلا سيليكون شفاف بزوايا مضادة للصدمات من بولي يوريثين مقاوم للحرارة لموبايل ايفون 15 برو من دلع موبايلك - شفاف',
    photo:
      'https://m.media-amazon.com/images/I/61mcJHA-0BL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CTBG3V21',
    asin: 'B0CTBG3V21',
  },
  {
    title:
      'جراب حماية فيوجن بولد شفاف ومتين بإطار صلب بتصميم مبتكر مقاوم للصدمات ومضاد للاصفرار متوافق مع موبايل ايفون 15 برو 6.1 بوصة (2023) من رينجكي، لون أسود',
    photo:
      'https://m.media-amazon.com/images/I/71hSq9NbvgL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CF2S2CJ2',
    asin: 'B0CF2S2CJ2',
  },
  {
    title:
      'شاشة حماية للخصوصية لموبايل آيفون 15 برو ماكس [6.1 بوصة] من بينكس، من زجاج مقوى عالي الدقة HD بدرجة صلابة 9 تصميم مضاد للتجسس والخدش وخالي من الفقاعات (قطعتان)، أسود',
    photo:
      'https://m.media-amazon.com/images/I/81eIM9b-RCL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CKF7P3HC',
    asin: 'B0CKF7P3HC',
  },
  {
    title:
      'جراب حماية شفاف الترا هايبرد (ماج فيت) من سبيجن، مصمم لموبايل ايفون 15/15 برو/15 بلس/ايفون 15 برو ماكس، جراب زيرو ون [تقنية مضادة للاصفرار] (ابل ايفون 15 برو ماكس، لون أسود)',
    photo:
      'https://m.media-amazon.com/images/I/71UgiXb0NiL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CMQC4913',
    asin: 'B0CMQC4913',
  },
  {
    title:
      'جراب حماية سيليكون بأشكال حلوى 3D لموبايل ايفون 15 برو ماكس|15 برو|15|14 برو ماكس|14 برو|14/13|13 برو ماكس|13 برو|12 برو ماكس|12/12 برو|11 برو ماكس|11 برو/11 (ستيتش نيو، ايفون 15 برو ماكس)',
    photo:
      'https://m.media-amazon.com/images/I/61yAOlYlIWL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0D96N1FW7',
    asin: 'B0D96N1FW7',
  },
  {
    title:
      'جراب حماية شفاف مضاد للانزلاق والسقوط ومقاوم للاصفرار متوافق مع شاحن ماج سيف لموبايل آيفون 15 برو للرجال والنساء من المعز، سيليكون',
    photo:
      'https://m.media-amazon.com/images/I/719ogMRzSiL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0D42CVQNY',
    asin: 'B0D42CVQNY',
  },
  {
    title:
      'نيلكن جراب موبايل لموبايل ايفون 15 برو، جراب كام شيلد برو 15 برو مع غطاء كاميرا، غطاء منزلق متوافق مع ايفون 15 برو 5G حافظة 6.1 بوصة (اسود)، البولي يوريثين الحراري',
    photo:
      'https://m.media-amazon.com/images/I/71PqTI8VatL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CFV9HQPF',
    asin: 'B0CFV9HQPF',
  },
  {
    title:
      'نيلكن جراب حماية رفيع مضاد للصدمات لموبايل ايفون 15 برو مع غطاء كاميرا منزلق، [حماية مطورة للعدسات]، [ممتص صدمات صلب من البولي كربونات والبولي يوريثين الحراري]، [مضاد للبصمات]، 6.1 انش 2023، رمادي',
    photo:
      'https://m.media-amazon.com/images/I/81S2SWEBXTL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CQQVLWLC',
    asin: 'B0CQQVLWLC',
  },
  {
    title:
      'ستيكر لاصق خلفي واقي شاشة جيلاتين مات مضاد لبصمات الاصابع لكامل الجانب ومضاد للخدش متوافق مع ايفون 15 برو، شفاف',
    photo:
      'https://m.media-amazon.com/images/I/41X42YXCKaL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0DBLX8954',
    asin: 'B0DBLX8954',
  },
  {
    title:
      'جراب مطلي بالكهرباء لموبايل ايفون 15 برو 6.1 بوصة من دلع موبايلك بحماية كاملة لعدسة الكاميرا - متوافق مع الشحن اللاسلكي ماج سيف من البولي يوريثين المقاوم للحرارة ال المضاد للصدمات (أسود) حافظة بسيطة',
    photo:
      'https://m.media-amazon.com/images/I/61kLuog2d6L._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CM7VYPZL',
    asin: 'B0CM7VYPZL',
  },
  {
    title:
      'دلـع موبايلك جراب حماية متوافق مع ايفون 15 برو 6.1 انش، غطاء حماية رفيع من السيليكون السائل من 3 طبقات من المطاط الناعم مع بطانة مضادة للخدش من المايكروفايبر (رمادي)',
    photo:
      'https://m.media-amazon.com/images/I/61KCYK-MURL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0D9PMM7BC',
    asin: 'B0D9PMM7BC',
  },
  {
    title:
      'شاشة حماية زجاج مقوى فائقة الشفافية HD مقاومة للخدش بدرجة صلابة 9H وخالية من الفقاعات بسمك 0.33 ملم لموبايل آيفون 15 برو مقاس 6.1 بوصة من نيو سي، 3 قطع، شفاف/أخضر',
    photo:
      'https://m.media-amazon.com/images/I/71O77qWEvXL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CJMJS3DS',
    asin: 'B0CJMJS3DS',
  },
  {
    title:
      'جراب حماية ماجيك من السيليكون الناعم مع بطانة من المايكروفايبر لموبايل ابل ايفون 15 برو 6.1 بوصة (أسود)، ألياف دقيقة',
    photo:
      'https://m.media-amazon.com/images/I/61UFxf93xBL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CTHVL7TT',
    asin: 'B0CTHVL7TT',
  },
  {
    title: 'جوي رووم واقي زجاجي للخصوصية لموبايل ايفون 15 برو JR-Z34، شفاف',
    photo:
      'https://m.media-amazon.com/images/I/61owqcTtL5L._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0D8JMHDMT',
    asin: 'B0D8JMHDMT',
  },
  {
    title:
      'نيلكن جراب حماية سوبر فروستد شيلد برو سيريز مصمم لموبايل ابل ايفون 15 برو (مع فتحة للشعار) - اسود، البولي يوريثين الحراري',
    photo:
      'https://m.media-amazon.com/images/I/716xiU-XpkL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CMQM5BPY',
    asin: 'B0CMQM5BPY',
  },
  {
    title:
      'جراب حماية سيليكون سائل لحماية كاملة لهيكل الموبايل ، جراب رفيع مضاد للخدش والصدمات ببطانة مايكروفايبر ناعمة لموبايل ايفون 15 pro مقاس 6.1 بوصة (كشمير)',
    photo:
      'https://m.media-amazon.com/images/I/31QbAj9kjcL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CM6SNVMQ',
    asin: 'B0CM6SNVMQ',
  },
  {
    title:
      'جراب حماية ماجيك من السيليكون الناعم مع بطانة من المايكروفايبر لموبايل ابل ايفون 15 برو 6.1 بوصة (لون زهري)، ألياف دقيقة',
    photo:
      'https://m.media-amazon.com/images/I/61IDZ35OTLL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CTHTPR3L',
    asin: 'B0CTHTPR3L',
  },
  {
    title:
      'جراب حماية ماجيك من السيليكون الناعم مع بطانة من المايكروفايبر لموبايل ابل ايفون 15 برو 6.1 بوصة (كحلي)، ألياف دقيقة',
    photo:
      'https://m.media-amazon.com/images/I/618EpqXxYHL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CTHRT6Q9',
    asin: 'B0CTHRT6Q9',
  },
  {
    title:
      'جراب فيوجن بولد مطفي متوافق مع ايفون 15 برو 6.1 بوصة (2023) من رينجكي، قبضة قوية [نمط الإطار] تصميم خلفي مصنفر صلب مضاد للصدمات ومضاد للاصفرار، أسود',
    photo:
      'https://m.media-amazon.com/images/I/719OEwKh0IL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CF2QQL3Z',
    asin: 'B0CF2QQL3Z',
  },
  {
    title:
      'جراب حماية خلفي فيوجن-اكس ممتص للصدمات من البولي يوريثين الحراري من رينجكي متوافق مع موبايل ايفون 15 برو 6.1 بوصة، [حماية من الدرجة العسكرية] حماية متقدمة من البولي كربونات، أسود مموه',
    photo:
      'https://m.media-amazon.com/images/I/81Rk9O3rGPL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CF2TMH9X',
    asin: 'B0CF2TMH9X',
  },
  {
    title:
      'جراب سيليكون بخياطة مموجة لموبايل ايفون 15 برو ماكس 14 14/13 13 12 12/12 11 برو/ مضاد للسقوط ( ) متعدد الألوان',
    photo:
      'https://m.media-amazon.com/images/I/71txZUzLELL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CVNFCCKC',
    asin: 'B0CVNFCCKC',
  },
  {
    title: 'ابل آيفون 15 128 جيجابايت - وردي، ايفون 15، بشريحتين',
    photo:
      'https://m.media-amazon.com/images/I/71PScsfMkRL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CHXHSD5X',
    asin: 'B0CHXHSD5X',
  },
  {
    title:
      'شاشة حماية من الزجاج المقوى بدرجة صلابة 9 لموبايل ابل ايفون 15 برو 15.4سم، شفاف',
    photo:
      'https://m.media-amazon.com/images/I/61uA99ekh8L._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CK2PHPFQ',
    asin: 'B0CK2PHPFQ',
  },
  {
    title:
      'جراب شفاف جير 4 كريستال بالاس مع حماية متقدمة من الصدمات وتصميم نحيف ومتين (آيفون 15 برو)',
    photo:
      'https://m.media-amazon.com/images/I/41KbA-XuwBL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0D55D5QKH',
    asin: 'B0D55D5QKH',
  },
  {
    title:
      'جراب فيوجن اكس متوافق مع آيفون 15 برو 6.1 بوصة، [حماية من الدرجة العسكرية] حماية متقدمة البولي كربونات + موبايل ممتص للصدمات يوريثين الحراري رينجكي، لون أسود .',
    photo:
      'https://m.media-amazon.com/images/I/71laLADw4QL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CF2SR65X',
    asin: 'B0CF2SR65X',
  },
  {
    title:
      'واقي من الزجاج المقوى لعدسة الكاميرا لموبايل ابل ايفون 15 برو ماكس / ايفون 15 برو، زجاج مُقسٌى، شفاف',
    photo:
      'https://m.media-amazon.com/images/I/71mm6QUrOsL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CKXQDSSW',
    asin: 'B0CKXQDSSW',
  },
  {
    title:
      'دلـع موبايلك جراب حماية متوافق مع ايفون 15 برو 6.1 انش، غطاء حماية رفيع من السيليكون السائل من 3 طبقات من المطاط الناعم مع بطانة مضادة للخدش من المايكروفايبر (ازرق)',
    photo:
      'https://m.media-amazon.com/images/I/61A0pM13QTL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0D9PMDV2T',
    asin: 'B0D9PMDV2T',
  },
  {
    title:
      'واقي عدسة الكاميرا متوافق مع ايفون 15 برو ماكس/ايفون 15 برو، واقي كاميرا من الزجاج المقوى بدرجة صلابة 9، مقاوم للخدش، لقطات ليلية عالية الدقة HD، غطاء حماية لعدسة الكاميرا من قطعتين، شفاف',
    photo:
      'https://m.media-amazon.com/images/I/61nhmFLwjML._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CGMYN9JS',
    asin: 'B0CGMYN9JS',
  },
  {
    title:
      'جراب حماية فيوجن بولد متوافق مع ايفون 15 برو 6.1 بوصة (2023) من رينجكي، جراب خلفي قوي صلب شفاف بنمط إطار مضاد للصدمات ومضاد للاصفرار - أسود',
    photo:
      'https://m.media-amazon.com/images/I/71y511OfehL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CF2TT846',
    asin: 'B0CF2TT846',
  },
  {
    title:
      'جراب متوافق مع موبايل ايفون 15 برو مع دعامة من الستيل، حامل مغناطيسي للسيارة، جراب حماية للموبايل (احمر نبيذي)، سيليكون',
    photo:
      'https://m.media-amazon.com/images/I/71JprZDN40L._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0DBWR98Z5',
    asin: 'B0DBWR98Z5',
  },
  {
    title:
      'دلـع موبايلك جراب متوافق مع موبايل ايفون 15 برو، جراب شفاف من البولي يوريثين الحراري الناعم من جل البولي يوريثين الحراري المرن ومضاد للخدش - شفاف',
    photo:
      'https://m.media-amazon.com/images/I/71bZK1Xw5UL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0D2S3MR83',
    asin: 'B0D2S3MR83',
  },
  {
    title:
      'جراب حماية منزلق لكاميرا نيلكين لموبايل ايفون 15 برو من نيلكين، متوافق مع موبايل ايفون 15 برو (ارجواني)، أكريلونيتريل بوتادين ستايرين',
    photo:
      'https://m.media-amazon.com/images/I/71ZmdzZ8BvL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CNQCF4VT',
    asin: 'B0CNQCF4VT',
  },
  {
    title:
      'جراب سيليكون باشكال حلوى ثلاثية الابعاد لموبايل ايفون 15 برو ماكس|15 برو|15|14 برو ماكس|14 برو|14/13|13 برو ماكس|13 برو|12 برو ماكس|12/12 برو|11 برو ماكس|11 برو/11 (توم وجيري اسود، ايفون 12 برو ماكس)',
    photo:
      'https://m.media-amazon.com/images/I/6163S-pQnzL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0D8G2N4PJ',
    asin: 'B0D8G2N4PJ',
  },
  {
    title:
      'متوافق مع Nillkin، متوافق مع ايفون 15 برو ، جراب متين غير لامع وشديد التحمل ومقاوم للصدمات، متوافق مع ايفون 15 برو TPU + جراب حماية مضاد للانزلاق من البولي كربونات Black، أسود',
    photo:
      'https://m.media-amazon.com/images/I/71aWAhWcX0L._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0CNQ9QCND',
    asin: 'B0CNQ9QCND',
  },
  {
    title:
      'واقي شاشة لهاتف ايفون 15 برو ماكس من انكر، مصنوع من الزجاج المقوى عالي الدقة خالي من الفقاعات والغبار، متين ومقاوم للسقوط بدرجة صلابة 9، سهل التركيب حصريًا لموبايل ايفون 15 برو',
    photo:
      'https://m.media-amazon.com/images/I/612UUlZ5wjL._AC_UL960_FMwebp_QL65_.jpg',
    url: 'https://www.amazon.eg/dp/B0D3FD7M9M',
    asin: 'B0D3FD7M9M',
  },
];

const demoRes = [
  {
    listing: {
      category_id: 7,
      category: 'Smartphones',
      whats_included: 'Apple iPhone and Charging Cable',
      attributes: [
        {
          name_en: 'OS',
          type_name: 'select',
          id: 56,
          official_values_en: ['iOS', 'Android', 'HarmonyOS'],
          value: 'iOS',
        },
        {
          name_en: 'Water & Dust resistance IP',
          type_name: 'select',
          id: 57,
          official_values_en: ['IP66', 'IP67', 'IP68'],
          value: null,
        },
        {
          name_en: '4G',
          type_name: 'boolean',
          id: 63,
          value: true,
        },
        {
          name_en: 'Screen Size',
          type_name: 'numeric',
          id: 61,
          value: 6.7,
        },
        {
          name_en: 'Wireless Charging',
          type_name: 'boolean',
          id: 60,
          value: null,
        },
        {
          name_en: 'Main Rear Camera Resolution',
          type_name: 'numeric',
          id: 58,
          value: 48,
        },
        {
          name_en: 'Storage',
          type_name: 'numeric',
          id: 54,
          value: 256,
        },
        {
          name_en: 'Main Front Camera Resolution',
          type_name: 'numeric',
          id: 59,
          value: null,
        },
        {
          name_en: '5G',
          type_name: 'boolean',
          id: 62,
          value: true,
        },
        {
          name_en: 'RAM',
          type_name: 'numeric',
          id: 55,
          value: 8,
        },
        {
          name_en: 'Bluetooth',
          type_name: 'boolean',
          id: 64,
          value: true,
        },
        {
          name_en: 'Brand',
          type_name: 'select',
          id: 1,
          official_values_en: [
            'Amazon',
            'Apple',
            'Google',
            'Huawei',
            'Lenovo',
            'Microsoft',
            'OnePlus',
            'Oppo',
            'Samsung',
            'Vivo',
            'Xiaomi',
            'Anker',
            'Belkin',
            'JBL',
            'OtterBox',
            'Sony',
            'Spigen',
            '-',
          ],
          value: 'Apple',
        },
        {
          name_en: 'Color',
          type_name: 'select',
          id: 53,
          official_values_en: [
            'White',
            'Black',
            'Red',
            'Green',
            'Yellow',
            'Blue',
            'Several colors',
          ],
          value: 'Blue',
        },
        {
          name_en: 'Model',
          type_name: 'select',
          id: 52,
          official_values_en: [
            'iPhone 15',
            'iPhone 15 Pro',
            'iPhone 15 Pro Max',
          ],
          value: 'iPhone 15 Pro Max',
        },
      ],
      main_image: 'https://m.media-amazon.com/images/I/3115lGL9zTL.jpg',
      all_images: [
        'https://m.media-amazon.com/images/I/3115lGL9zTL.jpg',
        'https://m.media-amazon.com/images/I/31+x0hVPhmL.jpg',
        'https://m.media-amazon.com/images/I/21zy46luI+L.jpg',
      ],
      custom_attributes: [
        {
          name_en: 'Operating System',
          type: 'select',
          value: 'iOS 16',
        },
        {
          name_en: 'Ram Memory Installed Size',
          type: 'select',
          value: '8 GB',
        },
        {
          name_en: 'Memory Storage Capacity',
          type: 'select',
          value: '256 GB',
        },
        {
          name_en: 'Model Name',
          type: 'select',
          value: 'iPhone 15 Pro Max',
        },
        {
          name_en: 'Wireless Carrier',
          type: 'select',
          value: 'Unlocked for All Carriers',
        },
        {
          name_en: 'Cellular Technology',
          type: 'select',
          value: '5G',
        },
        {
          name_en: 'Connectivity Technology',
          type: 'select',
          value: 'Bluetooth, Wi-Fi',
        },
        {
          name_en: 'Item model number',
          type: 'select',
          value: 'A2849',
        },
        {
          name_en: 'Batteries',
          type: 'select',
          value: '1 Lithium Ion batteries required. (included)',
        },
        {
          name_en: 'Connectivity technologies',
          type: 'select',
          value: 'Bluetooth, Wi-Fi',
        },
        {
          name_en: 'GPS',
          type: 'select',
          value: 'True',
        },
        {
          name_en: 'Special features',
          type: 'select',
          value: 'Dual SIM',
        },
        {
          name_en: 'Other display features',
          type: 'select',
          value: 'Wireless',
        },
        {
          name_en: 'Human Interface Input',
          type: 'select',
          value: 'Touchscreen',
        },
        {
          name_en: 'Form Factor',
          type: 'select',
          value: 'Bar',
        },
        {
          name_en: 'Battery Power Rating',
          type: 'select',
          value: '3600 Amp Hours',
        },
        {
          name_en: 'Whats in the box',
          type: 'select',
          value: 'Apple iPhone and Charging Cable',
        },
        {
          name_en: 'Manufacturer',
          type: 'select',
          value: 'Apple',
        },
        {
          name_en: 'Standing screen display size',
          type: 'select',
          value: '6.7 Inches',
        },
      ],
      product_dimensions: {
        height: 64.51599999999999,
        width: 12.9032,
        length: 17.419320000000003,
        original_value: '10 x 2 x 2.7 inches',
      },
      weight: 408.2328,
      title_en:
        'Apple iPhone 15 Pro Max - Renewed Premium, 256GB, Blue Titanium - Unlocked',
      description_en:
        "Experience the unparalleled performance of the Apple iPhone 15 Pro Max in stunning Blue Titanium. With a generous 256GB storage capacity and 8GB RAM, this renewed premium smartphone is equipped for all your needs. Its 6.7-inch Super Retina XDR display with ProMotion technology offers an exceptional viewing experience, while the A17 Pro chip ensures top-notch functionality with its 6-core GPU.\n\nCapture stunning images with the advanced pro camera system featuring a 48MP Main, Ultra Wide, and Telephoto lens, delivering super-high-resolution photos and next-generation portraits. Enjoy up to 29 hours of video playback on this powerful device that supports USB-C for rapid data transfers. Additionally, features like Emergency SOS via satellite and Crash Detection enhance your safety on the go.\n\nWhether you're browsing, gaming, or streaming, the iPhone 15 Pro Max can handle it all with ease. Get this smartphone today and elevate your mobile experience.",
      title_ar:
        'Apple iPhone 15 Pro Max - مجدد ممتاز، 256 جيجابايت، ع titanium زرقاء - غير مقفلة',
      description_ar:
        'استمتع بأداء لا مثيل له مع Apple iPhone 15 Pro Max بتصميم مذهل بلون تيانيوم الأزرق. يوفر هذا الهاتف الذكي المجدد الممتاز سعة تخزين كبيرة تبلغ 256 جيجابايت وذاكرة RAM 8 جيجابايت، مزودًا لجميع احتياجاتك. شاشة Super Retina XDR بحجم 6.7 بوصة مع تقنية ProMotion تقدم تجربة عرض استثنائية، بينما يضمن معالج A17 Pro أداءً متفوقًا مع وحدة معالجة الرسومات 6-core.\n\nالتقط صورًا رائعة مع نظام الكاميرا الاحترافي المتقدم الذي يتميز بكاميرا رئيسية بدقة 48 ميجابكسل وUltra Wide وTelephoto، حيث يقدم صوراً عالية الدقة وأحدث الصور الشخصية. استمتع بما يصل إلى 29 ساعة من وقت تشغيل الفيديو على هذا الجهاز القوي الذي يدعم USB-C لنقل البيانات بسرعة. كما تعزز ميزات مثل SOS الطارئة عبر الأقمار الصناعية وكشف الحوادث سلامتك أثناء التنقل.\n\nسواء كنت تتصفح أو تلعب أو تشاهد، يمكن لجهاز iPhone 15 Pro Max التعامل مع كل شيء بسهولة. احصل على هذا الهاتف الذكي اليوم وارتقِ بتجربتك المحمولة.',
    },
    asin: 'B0CRHZZLYR',
    amazon_api_response: {
      asin: 'B0CRHZZLYR',
      title:
        'Apple iPhone 15 Pro Max, 256GB, Blue Titanium - Unlocked (Renewed Premium)',
      about: [
        '6.7inch Super Retina XDR display. ProMotion technology. Always-On display. Titanium with textured matte glass back. Action button',
        'Dynamic Island. A magical way to interact with iPhone. A17 Pro chip with 6-core GPU',
        'Pro camera system. 48MP Main | Ultra Wide| Telephoto. Super-high-resolution photos (24MP and 48MP). Next-generation portraits with Focus and Depth Control. Up to 10x optical zoom range',
        'Emergency SOS via satellite. Crash Detection. Roadside Assistance via satellite',
        'Up to 29 hours video playback. USB-C, Supports USB 3 for up to 20x faster transfers. Face ID',
      ],
      details: {
        Brand: 'Apple',
        'Operating System': 'iOS 16',
        'Ram Memory Installed Size': '8 GB',
        'Memory Storage Capacity': '256 GB',
        'Screen Size': '6.7 Inches',
        'Model Name': 'iPhone 15 Pro Max',
        'Wireless Carrier': 'Unlocked for All Carriers',
        'Cellular Technology': '5G',
        'Connectivity Technology': 'Bluetooth, Wi-Fi',
        Color: 'Blue Titanium',
      },
      describtion:
        'Apple iPhone 15 Pro Max, 256GB, Blue Titanium - Unlocked (Renewed Premium)',
      information: {
        'Product Dimensions': '10 x 2 x 2.7 inches',
        'Item Weight': '14.4 ounces',
        ASIN: 'B0CRHZZLYR',
        'Item model number': 'A2849',
        Batteries: '1 Lithium Ion batteries required. (included)',
        'Best Sellers Rank':
          '#140 in Amazon Renewed (See Top 100 in Amazon Renewed)   #48 in Renewed Smartphones',
        OS: 'iOS 16',
        'Connectivity technologies': 'Bluetooth, Wi-Fi',
        GPS: 'True',
        'Special features': 'Dual SIM',
        'Other display features': 'Wireless',
        'Human Interface Input': 'Touchscreen',
        'Form Factor': 'Bar',
        Color: 'Blue Titanium',
        'Battery Power Rating': '3600 Amp Hours',
        'Whats in the box': 'Apple iPhone and Charging Cable',
        Manufacturer: 'Apple',
        'Date First Available': 'January 4, 2024',
        'Memory Storage Capacity': '256 GB',
        'Standing screen display size': '6.7 Inches',
        'Ram Memory Installed Size': '8 GB',
      },
      url: 'https://www.amazon.com/dp/B0CRHZZLYR',
      photo: 'https://m.media-amazon.com/images/I/3115lGL9zTL.jpg',
      photos: [
        'https://m.media-amazon.com/images/I/3115lGL9zTL.jpg',
        'https://m.media-amazon.com/images/I/31+x0hVPhmL.jpg',
        'https://m.media-amazon.com/images/I/21zy46luI+L.jpg',
      ],
      variations: {
        color: [
          {
            asin: 'B0CRJJBBPY',
            value: 'Black Titanium',
            photo: 'https://m.media-amazon.com/images/I/11lmjtsHP6L.jpg',
            is_available: true,
          },
          {
            asin: 'B0CRHZZLYR',
            value: 'Blue Titanium',
            photo: 'https://m.media-amazon.com/images/I/11fW1UmKueL.jpg',
            is_available: true,
          },
          {
            asin: 'B0CRJ2GWSM',
            value: 'Natural Titanium',
            photo: 'https://m.media-amazon.com/images/I/11DhTg0wucL.jpg',
            is_available: true,
          },
          {
            asin: 'B0CRJXJS12',
            value: 'White Titanium',
            photo: 'https://m.media-amazon.com/images/I/01wjNKcMMcL.jpg',
            is_available: false,
          },
        ],
        size: [
          {
            asin: 'B0CRJXJS12',
            value: '1TB',
            is_available: false,
          },
          {
            asin: 'B0CRHZZLYR',
            value: '256GB',
            is_available: true,
          },
          {
            asin: 'B0CRHZM1SD',
            value: '512GB',
            is_available: true,
          },
        ],
      },
    },
  },
  {
    listing: {
      whats_included: 'Apple iPhone and Charging Cable',
      category_id: 7,
      category: 'Smartphones',
      attributes: [
        {
          name_en: 'OS',
          type_name: 'select',
          id: 56,
          official_values_en: ['iOS', 'Android', 'HarmonyOS'],
          value: 'iOS',
        },
        {
          name_en: 'Water & Dust resistance IP',
          type_name: 'select',
          id: 57,
          official_values_en: ['IP66', 'IP67', 'IP68'],
          value: null,
        },
        {
          name_en: '4G',
          type_name: 'boolean',
          id: 63,
          value: true,
        },
        {
          name_en: 'Screen Size',
          type_name: 'numeric',
          id: 61,
          value: 6.7,
        },
        {
          name_en: 'Wireless Charging',
          type_name: 'boolean',
          id: 60,
          value: null,
        },
        {
          name_en: 'Main Rear Camera Resolution',
          type_name: 'numeric',
          id: 58,
          value: 48,
        },
        {
          name_en: 'Storage',
          type_name: 'numeric',
          id: 54,
          value: 256,
        },
        {
          name_en: 'Main Front Camera Resolution',
          type_name: 'numeric',
          id: 59,
          value: null,
        },
        {
          name_en: '5G',
          type_name: 'boolean',
          id: 62,
          value: true,
        },
        {
          name_en: 'RAM',
          type_name: 'numeric',
          id: 55,
          value: 8,
        },
        {
          name_en: 'Bluetooth',
          type_name: 'boolean',
          id: 64,
          value: true,
        },
        {
          name_en: 'Brand',
          type_name: 'select',
          id: 1,
          official_values_en: [
            'Amazon',
            'Apple',
            'Google',
            'Huawei',
            'Lenovo',
            'Microsoft',
            'OnePlus',
            'Oppo',
            'Samsung',
            'Vivo',
            'Xiaomi',
            'Anker',
            'Belkin',
            'JBL',
            'OtterBox',
            'Sony',
            'Spigen',
            '-',
          ],
          value: 'Apple',
        },
        {
          name_en: 'Color',
          type_name: 'select',
          id: 53,
          official_values_en: [
            'White',
            'Black',
            'Red',
            'Green',
            'Yellow',
            'Blue',
            'Several colors',
          ],
          value: 'Natural Titanium',
        },
        {
          name_en: 'Model',
          type_name: 'select',
          id: 52,
          official_values_en: [
            'iPhone 15',
            'iPhone 15 Pro',
            'iPhone 15 Pro Max',
          ],
          value: 'iPhone 15 Pro Max',
        },
      ],
      main_image: 'https://m.media-amazon.com/images/I/31GaGWsjGyL.jpg',
      all_images: [
        'https://m.media-amazon.com/images/I/31GaGWsjGyL.jpg',
        'https://m.media-amazon.com/images/I/21j-913NpgL.jpg',
        'https://m.media-amazon.com/images/I/21pMnZaOTcL.jpg',
      ],
      custom_attributes: [
        {
          name_en: 'Operating System',
          type: 'select',
          value: 'iOS 16',
        },
        {
          name_en: 'Ram Memory Installed Size',
          type: 'select',
          value: '8 GB',
        },
        {
          name_en: 'Memory Storage Capacity',
          type: 'select',
          value: '256 GB',
        },
        {
          name_en: 'Model Name',
          type: 'select',
          value: 'iPhone 15 Pro Max',
        },
        {
          name_en: 'Wireless Carrier',
          type: 'select',
          value: 'Unlocked for All Carriers',
        },
        {
          name_en: 'Cellular Technology',
          type: 'select',
          value: '5G',
        },
        {
          name_en: 'Connectivity Technology',
          type: 'select',
          value: 'Bluetooth, Wi-Fi',
        },
        {
          name_en: 'Item model number',
          type: 'select',
          value: 'A2849',
        },
        {
          name_en: 'Batteries',
          type: 'select',
          value: '1 Lithium Ion batteries required. (included)',
        },
        {
          name_en: 'Wireless communication technologies',
          type: 'select',
          value: 'Cellular',
        },
        {
          name_en: 'Connectivity technologies',
          type: 'select',
          value: 'Bluetooth, Wi-Fi',
        },
        {
          name_en: 'GPS',
          type: 'select',
          value: 'True',
        },
        {
          name_en: 'Special features',
          type: 'select',
          value: 'Dual SIM',
        },
        {
          name_en: 'Other display features',
          type: 'select',
          value: 'Wireless',
        },
        {
          name_en: 'Human Interface Input',
          type: 'select',
          value: 'Touchscreen',
        },
        {
          name_en: 'Form Factor',
          type: 'select',
          value: 'Bar',
        },
        {
          name_en: 'Battery Power Rating',
          type: 'select',
          value: '3600 Amp Hours',
        },
        {
          name_en: 'Whats in the box',
          type: 'select',
          value: 'Apple iPhone and Charging Cable',
        },
        {
          name_en: 'Manufacturer',
          type: 'select',
          value: 'Apple',
        },
        {
          name_en: 'Standing screen display size',
          type: 'select',
          value: '6.7 Inches',
        },
      ],
      product_dimensions: {
        height: 64.51599999999999,
        width: 12.9032,
        length: 17.419320000000003,
        original_value: '10 x 2 x 2.7 inches',
      },
      weight: 439.41724999999997,
      title_en:
        'Apple iPhone 15 Pro Max - 256GB, Unlocked in Natural Titanium (Renewed Premium)',
      description_en:
        'Experience the power of the Apple iPhone 15 Pro Max, featuring a stunning 6.7-inch Super Retina XDR display with ProMotion technology for ultra-smooth scrolling. This Premium Renewed device includes a powerful A17 Pro chip and a versatile Pro camera system with 48MP Main, Ultra Wide, and Telephoto lenses for breathtaking photography. Enjoy advanced features like Dynamic Island, Emergency SOS via satellite, and up to 29 hours of video playback. With 256GB of storage and 8GB of RAM, this unlocked smartphone is perfect for any carrier. Comes with a charging cable in the box.',
      title_ar:
        'آبل آيفون 15 برو ماكس - 256 جيجابايت، مفتوح في تيتانيوم طبيعي (مُجدَّد بشكل ممتاز)',
      description_ar:
        'اختبر قوة آبل آيفون 15 برو ماكس، والذي يتميز بشاشة 6.7 بوصة من نوع Super Retina XDR مع تقنية ProMotion لتجربة تمرير فائقة السلاسة. يتضمن الجهاز المُجدَّد الممتاز شريحة A17 Pro القوية ونظام كاميرا احترافي مع عدسات 48 ميجابكسل للرئيسية، وواسعة الزاوية، والتلفوتو، لالتقاط صور مذهلة. استمتع بمميزات متقدمة مثل Dynamic Island، وSOS الطارئ عبر الأقمار الصناعية، ومدة تشغيل تصل إلى 29 ساعة من الفيديو. مع 256 جيجابايت من التخزين و8 جيجابايت من الذاكرة العشوائية، هذا الهاتف الذكي غير مقفل مثالي لأي مشغل. يأتي مع كابل شحن في العلبة.',
    },
    asin: 'B0CRJSBN5M',
    amazon_api_response: {
      asin: 'B0CRJSBN5M',
      title:
        'Apple iPhone 15 Pro Max, 256GB, Natural Titanium - Unlocked (Renewed Premium)',
      about: [
        '6.7inch Super Retina XDR display. ProMotion technology. Always-On display. Titanium with textured matte glass back. Action button',
        'Dynamic Island. A magical way to interact with iPhone. A17 Pro chip with 6-core GPU',
        'Pro camera system. 48MP Main | Ultra Wide| Telephoto. Super-high-resolution photos (24MP and 48MP). Next-generation portraits with Focus and Depth Control. Up to 10x optical zoom range',
        'Emergency SOS via satellite. Crash Detection. Roadside Assistance via satellite',
        'Up to 29 hours video playback. USB-C, Supports USB 3 for up to 20x faster transfers. Face ID',
      ],
      details: {
        Brand: 'Apple',
        'Operating System': 'iOS 16',
        'Ram Memory Installed Size': '8 GB',
        'Memory Storage Capacity': '256 GB',
        'Screen Size': '6.7 Inches',
        'Model Name': 'iPhone 15 Pro Max',
        'Wireless Carrier': 'Unlocked for All Carriers',
        'Cellular Technology': '5G',
        'Connectivity Technology': 'Bluetooth, Wi-Fi',
        Color: 'Natural Titanium',
      },
      describtion:
        'Apple iPhone 15 Pro Max, 256GB, Natural Titanium - Unlocked (Renewed Premium)',
      information: {
        'Product Dimensions': '10 x 2 x 2.7 inches',
        'Item Weight': '15.5 ounces',
        ASIN: 'B0CRJSBN5M',
        'Item model number': 'A2849',
        Batteries: '1 Lithium Ion batteries required. (included)',
        'Best Sellers Rank':
          '#438 in Amazon Renewed (See Top 100 in Amazon Renewed)   #107 in Renewed Smartphones',
        OS: 'iOS 16',
        'Wireless communication technologies': 'Cellular',
        'Connectivity technologies': 'Bluetooth, Wi-Fi',
        GPS: 'True',
        'Special features': 'Dual SIM',
        'Other display features': 'Wireless',
        'Human Interface Input': 'Touchscreen',
        'Form Factor': 'Bar',
        Color: 'Natural Titanium',
        'Battery Power Rating': '3600 Amp Hours',
        'Whats in the box': 'Apple iPhone and Charging Cable',
        Manufacturer: 'Apple',
        'Date First Available': 'January 4, 2024',
        'Memory Storage Capacity': '256 GB',
        'Standing screen display size': '6.7 Inches',
        'Ram Memory Installed Size': '8 GB',
      },
      url: 'https://www.amazon.com/dp/B0CRJSBN5M',
      photo: 'https://m.media-amazon.com/images/I/31GaGWsjGyL.jpg',
      photos: [
        'https://m.media-amazon.com/images/I/31GaGWsjGyL.jpg',
        'https://m.media-amazon.com/images/I/21j-913NpgL.jpg',
        'https://m.media-amazon.com/images/I/21pMnZaOTcL.jpg',
      ],
      variations: [],
    },
  },
  {
    listing: {
      whats_included: 'Apple iPhone and Charging Cable',
      category_id: 7,
      category: 'Smartphones',
      attributes: [
        {
          name_en: 'OS',
          type_name: 'select',
          id: 56,
          official_values_en: ['iOS', 'Android', 'HarmonyOS'],
          value: 'iOS',
        },
        {
          name_en: 'Water & Dust resistance IP',
          type_name: 'select',
          id: 57,
          official_values_en: ['IP66', 'IP67', 'IP68'],
          value: null,
        },
        {
          name_en: '4G',
          type_name: 'boolean',
          id: 63,
          value: true,
        },
        {
          name_en: 'Screen Size',
          type_name: 'numeric',
          id: 61,
          value: 6.7,
        },
        {
          name_en: 'Wireless Charging',
          type_name: 'boolean',
          id: 60,
          value: null,
        },
        {
          name_en: 'Main Rear Camera Resolution',
          type_name: 'numeric',
          id: 58,
          value: 48,
        },
        {
          name_en: 'Storage',
          type_name: 'numeric',
          id: 54,
          value: 256,
        },
        {
          name_en: 'Main Front Camera Resolution',
          type_name: 'numeric',
          id: 59,
          value: null,
        },
        {
          name_en: '5G',
          type_name: 'boolean',
          id: 62,
          value: true,
        },
        {
          name_en: 'RAM',
          type_name: 'numeric',
          id: 55,
          value: 8,
        },
        {
          name_en: 'Bluetooth',
          type_name: 'boolean',
          id: 64,
          value: true,
        },
        {
          name_en: 'Brand',
          type_name: 'select',
          id: 1,
          official_values_en: [
            'Amazon',
            'Apple',
            'Google',
            'Huawei',
            'Lenovo',
            'Microsoft',
            'OnePlus',
            'Oppo',
            'Samsung',
            'Vivo',
            'Xiaomi',
            'Anker',
            'Belkin',
            'JBL',
            'OtterBox',
            'Sony',
            'Spigen',
            '-',
          ],
          value: 'Apple',
        },
        {
          name_en: 'Color',
          type_name: 'select',
          id: 53,
          official_values_en: [
            'White',
            'Black',
            'Red',
            'Green',
            'Yellow',
            'Blue',
            'Several colors',
          ],
          value: 'White',
        },
        {
          name_en: 'Model',
          type_name: 'select',
          id: 52,
          official_values_en: [
            'iPhone 15',
            'iPhone 15 Pro',
            'iPhone 15 Pro Max',
          ],
          value: 'iPhone 15 Pro Max',
        },
      ],
      main_image: 'https://m.media-amazon.com/images/I/41Xmk3ZGScL.jpg',
      all_images: [
        'https://m.media-amazon.com/images/I/41Xmk3ZGScL.jpg',
        'https://m.media-amazon.com/images/I/21LSiWiSGfL.jpg',
        'https://m.media-amazon.com/images/I/41xIi1rM9sL.jpg',
      ],
      custom_attributes: [
        {
          name_en: 'Operating System',
          type: 'select',
          value: 'iOS 17',
        },
        {
          name_en: 'Ram Memory Installed Size',
          type: 'select',
          value: '8 GB',
        },
        {
          name_en: 'Memory Storage Capacity',
          type: 'select',
          value: '256 GB',
        },
        {
          name_en: 'Model Name',
          type: 'select',
          value: 'iPhone 15 Pro Max',
        },
        {
          name_en: 'Wireless Carrier',
          type: 'select',
          value: 'Unlocked for All Carriers',
        },
        {
          name_en: 'Cellular Technology',
          type: 'select',
          value: '5G',
        },
        {
          name_en: 'Connectivity Technology',
          type: 'select',
          value: 'Bluetooth',
        },
        {
          name_en: 'Item model number',
          type: 'select',
          value: 'MU673LL/A',
        },
        {
          name_en: 'Batteries',
          type: 'select',
          value: '1 Lithium Ion batteries required. (included)',
        },
        {
          name_en: 'Wireless communication technologies',
          type: 'select',
          value: 'Cellular',
        },
        {
          name_en: 'Connectivity technologies',
          type: 'select',
          value: 'Bluetooth',
        },
        {
          name_en: 'Other display features',
          type: 'select',
          value: 'Wireless',
        },
        {
          name_en: 'Human Interface Input',
          type: 'select',
          value: 'Touchscreen',
        },
        {
          name_en: 'Form Factor',
          type: 'select',
          value: 'Slider',
        },
        {
          name_en: 'Battery Power Rating',
          type: 'select',
          value: '3600 Amp Hours',
        },
        {
          name_en: 'Whats in the box',
          type: 'select',
          value: 'Apple iPhone, USB Charging Cable',
        },
        {
          name_en: 'Department',
          type: 'select',
          value: 'Cell Phones & Accessories',
        },
        {
          name_en: 'Manufacturer',
          type: 'select',
          value: 'Apple',
        },
        {
          name_en: 'Standing screen display size',
          type: 'select',
          value: '6.7 Inches',
        },
      ],
      product_dimensions: {
        height: 64.51599999999999,
        width: 12.9032,
        length: 17.419320000000003,
        original_value: '10 x 2 x 2.7 inches',
      },
      weight: 317.51439999999997,
      title_en:
        'Apple iPhone 15 Pro Max - 256GB White Titanium (Renewed) | Unlocked Smartphone with A17 Pro Chip',
      description_en:
        "Experience the ultimate in mobile technology with the Apple iPhone 15 Pro Max, featuring a stunning 6.7-inch Super Retina XDR display and ProMotion technology for smooth scrolling. The sleek White Titanium finish complements the innovative design, including an Action Button and the magical Dynamic Island for seamless interaction. Powered by the A17 Pro chip with a 6-core GPU, it delivers exceptional performance for gaming and multitasking. Capture stunning photos with the pro camera system featuring 48MP Main, Ultra Wide, and Telephoto lenses, plus up to 10x optical zoom. Stay connected with 5G technology and enjoy emergency SOS via satellite, crash detection, and exceptional battery life with up to 29 hours of video playback. This renewed device comes unlocked for all carriers, making it the perfect choice for tech-savvy users. What's in the box? Your new iPhone along with a USB charging cable.",
      title_ar:
        'أبل آيفون 15 برو ماكس - 256 جيجابايت بلون الأبيض التيتانيوم (مُجدّد) | هاتف ذكي غير مقفل بمعالج A17 برو',
      description_ar:
        'اختبر قمة تقنية الهواتف المحمولة مع أبل آيفون 15 برو ماكس، الذي يتميز بشاشة سوبر ريتينا XDR مقاس 6.7 بوصة وتقنية ProMotion لتجربة تمرير سلسة. يكمل لمسة اللون الأبيض التيتانيوم التصميم المبتكر، بما في ذلك زر العمل وDynamic Island السحري للتفاعل السلس. مزود بمعالج A17 برو مع معالج رسومي سداسي النواة، يقدم أداءً استثنائيًا للألعاب والمهام المتعددة. التقط صوراً مذهلة مع نظام الكاميرا الاحترافي الذي يضم 48 ميجابكسل رئيسية، زاوية عريضة، وعدسة تليفوتوغرافي، بالإضافة إلى تقريب بصري يصل حتى 10x. ابق على اتصال مع تقنية 5G وتمتع بخدمات SOS الطارئة عبر الأقمار الصناعية، واكتشاف الحوادث، وزمن تشغيل البطارية الاستثنائي الذي يصل حتى 29 ساعة من تشغيل الفيديو. يأتي هذا الجهاز المُجدّد غير مقفل لجميع الشبكات، مما يجعله الخيار المثالي لمستخدمي التكنولوجيا المتطورة. ماذا في الصندوق؟ آيفون الجديد الخاص بك مع كابل شحن USB.',
    },
    asin: 'B0CMZDY2ZV',
    amazon_api_response: {
      asin: 'B0CMZDY2ZV',
      title:
        'Apple iPhone 15 Pro Max, 256GB, White Titanium - Unlocked (Renewed)',
      about: [
        '6.7inch Super Retina XDR display. ProMotion technology. Always-On display. Titanium with textured matte glass back. Action button',
        'Dynamic Island. A magical way to interact with iPhone. A17 Pro chip with 6-core GPU',
        'Pro camera system. 48MP Main | Ultra Wide| Telephoto. Super-high-resolution photos (24MP and 48MP). Next-generation portraits with Focus and Depth Control. Up to 10x optical zoom range',
        'Emergency SOS via satellite. Crash Detection. Roadside Assistance via satellite',
        'Up to 29 hours video playback. USB-C, Supports USB 3 for up to 20x faster transfers. Face ID',
      ],
      details: {
        Brand: 'Apple',
        'Operating System': 'iOS 17',
        'Ram Memory Installed Size': '8 GB',
        'Memory Storage Capacity': '256 GB',
        'Screen Size': '6.7 Inches',
        'Model Name': 'iPhone 15 Pro Max',
        'Wireless Carrier': 'Unlocked for All Carriers',
        'Cellular Technology': '5G',
        'Connectivity Technology': 'Bluetooth',
        Color: 'White Titanium',
      },
      describtion:
        'Apple iPhone 15 Pro Max, 256GB, White Titanium - Unlocked (Renewed)',
      information: {
        'Product Dimensions': '10 x 2 x 2.7 inches',
        'Item Weight': '11.2 ounces',
        ASIN: 'B0CMZDY2ZV',
        'Item model number': 'MU673LL/A',
        Batteries: '1 Lithium Ion batteries required. (included)',
        'Best Sellers Rank':
          '#188 in Amazon Renewed (See Top 100 in Amazon Renewed)   #60 in Renewed Smartphones',
        OS: 'iOS 17',
        'Wireless communication technologies': 'Cellular',
        'Connectivity technologies': 'Bluetooth',
        'Other display features': 'Wireless',
        'Human Interface Input': 'Touchscreen',
        'Form Factor': 'Slider',
        Color: 'White Titanium',
        'Battery Power Rating': '3600 Amp Hours',
        'Whats in the box': 'Apple iPhone, USB Charging Cable',
        Department: 'Cell Phones & Accessories',
        Manufacturer: 'Apple',
        'Date First Available': 'November 9, 2023',
        'Memory Storage Capacity': '256 GB',
        'Standing screen display size': '6.7 Inches',
        'Ram Memory Installed Size': '8 GB',
      },
      url: 'https://www.amazon.com/dp/B0CMZDY2ZV',
      photo: 'https://m.media-amazon.com/images/I/41Xmk3ZGScL.jpg',
      photos: [
        'https://m.media-amazon.com/images/I/41Xmk3ZGScL.jpg',
        'https://m.media-amazon.com/images/I/21LSiWiSGfL.jpg',
        'https://m.media-amazon.com/images/I/41xIi1rM9sL.jpg',
      ],
      variations: {
        color: [
          {
            asin: 'B0CMZ4FQL4',
            value: 'Black Titanium',
            photo: 'https://m.media-amazon.com/images/I/41+GAQkCssL.jpg',
            is_available: true,
          },
          {
            asin: 'B0CMZDBVWT',
            value: 'Blue Titanium',
            photo: 'https://m.media-amazon.com/images/I/41pFTEa5EVL.jpg',
            is_available: true,
          },
          {
            asin: 'B0CMZD7VCV',
            value: 'Natural Titanium',
            photo: 'https://m.media-amazon.com/images/I/41jVpEVgrRL.jpg',
            is_available: true,
          },
          {
            asin: 'B0CMZDY2ZV',
            value: 'White Titanium',
            photo: 'https://m.media-amazon.com/images/I/41Xmk3ZGScL.jpg',
            is_available: true,
          },
        ],
        size: [
          {
            asin: 'B0CMZLLQHT',
            value: '1TB',
            is_available: true,
          },
          {
            asin: 'B0CMZDY2ZV',
            value: '256GB',
            is_available: true,
          },
          {
            asin: 'B0CMYYNFZ6',
            value: '512GB',
            is_available: true,
          },
        ],
      },
    },
  },
];
